import React from 'react';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { HeroSectionProps } from '@/types/sections';
import { isVideoSource } from '@/lib/video-utils';
import { getBrandColor } from '@/config/brand-colors';

// Dynamically import client component for interactive parts
const HeroClient = dynamic(
  () => import('./HeroClient').then(mod => ({ default: mod.HeroClient })),
  { ssr: false }
);

export default function Hero({
  title,
  subtitle,
  backgroundImage,
  backgroundVideo,
  backgroundMediaFallback,
  backgroundMediaFallbackMobile,
  ctaText,
  ctaLink,
  secondaryCtaText,
  secondaryCtaLink,
  description
}: HeroSectionProps) {
  // Determine which video source to use (server-side)
  const videoSource = backgroundVideo || backgroundImage || null;
  
  // Determine if video source is actually a video (server-side)
  const hasVideo = (() => {
    if (!videoSource) {
      // #region agent log
      if (typeof window === 'undefined') {
        // Server-side logging via console (will be captured)
        console.log('[Hero.tsx] videoSource is null/undefined', { backgroundVideo, backgroundImage });
      }
      // #endregion
      return false;
    }
    if (typeof videoSource === 'string') {
      const result = isVideoSource(videoSource);
      // #region agent log
      if (typeof window === 'undefined') {
        console.log('[Hero.tsx] videoSource is string', { videoSource, isVideo: result });
      }
      // #endregion
      return result;
    }
    // Check both desktop and mobile URLs
    const desktopIsVideo = videoSource.desktop ? isVideoSource(videoSource.desktop) : false;
    const mobileIsVideo = videoSource.mobile ? isVideoSource(videoSource.mobile) : false;
    const result = desktopIsVideo || mobileIsVideo;
    // #region agent log
    if (typeof window === 'undefined') {
      console.log('[Hero.tsx] videoSource is responsive object', { 
        desktop: videoSource.desktop, 
        mobile: videoSource.mobile,
        desktopIsVideo,
        mobileIsVideo,
        hasVideo: result
      });
    }
    // #endregion
    return result;
  })();

  // Combine desktop and mobile fallback images into responsive object
  // This ensures BackgroundVideo receives the correct fallback for mobile/desktop
  const responsiveFallbackImage = (() => {
    // If we have both desktop and mobile fallbacks, create responsive object
    if (backgroundMediaFallbackMobile) {
      const desktopFallback = typeof backgroundMediaFallback === 'string' 
        ? backgroundMediaFallback 
        : (typeof backgroundMediaFallback === 'object' && backgroundMediaFallback !== null && 'desktop' in backgroundMediaFallback)
          ? backgroundMediaFallback.desktop
          : undefined;
      
      return {
        desktop: desktopFallback || backgroundMediaFallbackMobile,
        mobile: backgroundMediaFallbackMobile,
      };
    }
    
    // Otherwise, return desktop fallback as-is (can be string or responsive object)
    return backgroundMediaFallback;
  })();


  // Preload media using mediaCache (this hook needs to be called, but we'll handle it differently)
  // Note: useSectionMediaPreload is a client hook, so we'll need to handle preloading in the client component
  // For now, we'll rely on Next.js Image optimization for static images

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center md:items-end md:justify-end overflow-hidden">
      {/* Video Background - Rendered by client component after first paint */}
      {/* Static Image Background - Render immediately for LCP */}
      {backgroundImage && !hasVideo && (
        <div className="absolute inset-0 z-0 w-full h-full">
          {typeof backgroundImage === 'string' ? (
            <Image
              src={backgroundImage}
              alt="Background"
              fill
              priority
              sizes="100vw"
              className="object-cover"
              quality={85}
              fetchPriority="high"
            />
          ) : (
            <>
              {backgroundImage.desktop && (
                <Image
                  src={backgroundImage.desktop}
                  alt="Background"
                  fill
                  priority
                  sizes="100vw"
                  className="hidden md:block object-cover"
                  quality={85}
                  fetchPriority="high"
                />
              )}
              {backgroundImage.mobile && (
                <Image
                  src={backgroundImage.mobile}
                  alt="Background"
                  fill
                  priority
                  sizes="100vw"
                  className="block md:hidden object-cover"
                  quality={85}
                  fetchPriority="high"
                />
              )}
            </>
          )}
          <div className="absolute inset-0 bg-black/50 z-10" />
        </div>
      )}

      {/* Fallback background color if no image/video */}
      {!backgroundImage && !hasVideo && (
        <div 
          className="absolute inset-0 z-0 w-full h-full"
          style={{ backgroundColor: getBrandColor('deepNavy') }}
        />
      )}

      <style dangerouslySetInnerHTML={{ __html: `
        .hero-title-responsive {
          font-size: clamp(1.5rem, 6vw, 4rem) !important;
          line-height: 1 !important;
        }
        .hero-subtitle-responsive {
          font-size: clamp(0.875rem, 2.5vw, 2.25rem) !important;
          line-height: 1.1 !important;
        }
        @media (orientation: landscape) and (max-width: 1023px) {
          .hero-title-responsive {
            font-size: clamp(1.5rem, 4vw, 3rem) !important;
          }
          .hero-subtitle-responsive {
            font-size: clamp(0.875rem, 1.5vw, 1.5rem) !important;
          }
        }
        @media (min-width: 1024px) {
          .hero-title-responsive {
            font-size: clamp(1.5rem, 3.5vw, 3rem) !important;
          }
          .hero-subtitle-responsive {
            font-size: clamp(0.875rem, 1.5vw, 1.5rem) !important;
          }
        }
        @media (min-width: 1280px) {
          .hero-title-responsive {
            font-size: clamp(1.5rem, 3vw, 2.5rem) !important;
          }
          .hero-subtitle-responsive {
            font-size: clamp(0.875rem, 1.25vw, 1.5rem) !important;
          }
        }
      `}} />
      
      {/* Content - Render immediately for LCP */}
      <div className="container mx-auto px-4 relative w-full md:pb-20 md:pr-8 z-20">
        <div className="max-w-3xl mx-auto md:max-w-none md:ml-auto md:mr-0 md:flex md:flex-col md:items-end md:gap-6 md:justify-start">
          {/* Text Content - Static HTML, no JS needed */}
          <div className="text-center md:text-right">
            {title && (
              <h1
                className="hero-title-responsive font-bold mb-1 md:mb-0.5 text-white"
                style={{ 
                  opacity: 1, 
                  transform: 'none',
                  willChange: 'auto',
                  contentVisibility: 'auto',
                }}
                suppressHydrationWarning
              >
                {title}
              </h1>
            )}
            {/* Static description - render immediately, will be replaced by animated version when JS loads */}
            {description && (
              <div
                className="hero-subtitle-responsive text-white/90 leading-none mt-1 md:mt-0.5 md:max-w-[600px] md:ml-auto prose prose-invert max-w-none [&_p]:my-1 [&_p]:text-white/90 [&_h1]:text-white [&_h2]:text-white [&_h3]:text-white [&_strong]:text-white [&_em]:text-white/90"
                dangerouslySetInnerHTML={{ __html: description }}
                suppressHydrationWarning
              />
            )}
          </div>
          
          {/* Static CTA Buttons - Render immediately, will be enhanced by client component */}
          {(ctaText || secondaryCtaText) && (
            <div className="flex flex-row md:flex-row gap-4 justify-center md:justify-start items-center md:items-start mt-6 md:mt-0 relative z-30">
              {ctaText && ctaLink && (
                <a
                  href={ctaLink}
                  className="inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 h-12 px-6 text-sm md:h-16 md:px-12 md:text-lg relative z-40 whitespace-nowrap"
                  style={{
                    backgroundColor: getBrandColor('primaryBlue'),
                    color: getBrandColor('white'),
                    border: 'none'
                  }}
                >
                  {ctaText}
                  <svg className="ml-2 md:ml-3 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              )}
              {secondaryCtaText && (
                <a
                  href={secondaryCtaLink || '#'}
                  className="inline-flex items-center justify-center rounded-md font-medium transition-all duration-200 h-12 px-6 border-2 text-white hover:bg-white hover:text-deep-navy text-sm md:h-16 md:px-12 md:text-lg relative z-40 whitespace-nowrap"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderColor: 'white',
                    color: 'white'
                  }}
                >
                  {secondaryCtaText}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Client Component for interactive parts - Loaded after first paint */}
      {/* #region agent log */}
      {typeof window === 'undefined' && (
        <script
          dangerouslySetInnerHTML={{
            __html: `console.log('[Hero.tsx SSR] Props passed to HeroClient:', {
              hasVideo: ${hasVideo},
              videoSource: ${JSON.stringify(videoSource)},
              backgroundImage: ${JSON.stringify(backgroundImage)},
              backgroundVideo: ${JSON.stringify(backgroundVideo)}
            });`
          }}
        />
      )}
      {/* #endregion */}
      <HeroClient
        videoSource={videoSource}
        hasVideo={hasVideo}
        backgroundMediaFallback={responsiveFallbackImage}
        description={description}
        ctaText={ctaText}
        ctaLink={ctaLink}
        secondaryCtaText={secondaryCtaText}
        secondaryCtaLink={secondaryCtaLink}
        backgroundImage={backgroundImage}
        backgroundVideo={backgroundVideo}
      />
    </section>
  );
}
